import { useEffect, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { parseFenBoard, type ChessHttpClient, type GameStateResponse, type GameStatus, type PlayerColor } from '@chess/core';

const LONG_PRESS_MS = 500;

const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

function squareAt(rankIndex: number, fileIndex: number, flipped: boolean): string {
  const rank = flipped ? rankIndex : 7 - rankIndex;
  const file = flipped ? 7 - fileIndex : fileIndex;
  return `${String.fromCharCode(97 + file)}${rank + 1}`;
}

export function parseFenTurn(fen: string): PlayerColor {
  return fen.split(' ')[1] === 'b' ? 'black' : 'white';
}

const PIECE_SYMBOLS: Record<string, string> = {
  white_k: '♔',
  white_q: '♕',
  white_r: '♖',
  white_b: '♗',
  white_n: '♘',
  white_p: '♙',
  black_k: '♚',
  black_q: '♛',
  black_r: '♜',
  black_b: '♝',
  black_n: '♞',
  black_p: '♟',
};

interface BoardProps {
  fen?: string;
  playerColor: PlayerColor;
  turn: PlayerColor;
  status: GameStatus;
  gameId: string;
  token: string;
  client: ChessHttpClient;
  drawOfferedBy?: PlayerColor;
  onMoveApplied: (update: { fen: string; from: string; to: string }) => void;
  onGameStateUpdate: (update: Partial<GameStateResponse>) => void;
}

export function Board({
  fen = START_FEN,
  playerColor,
  turn,
  status,
  gameId,
  token,
  client,
  drawOfferedBy,
  onMoveApplied,
  onGameStateUpdate,
}: BoardProps) {
  const parsedGrid = parseFenBoard(fen);
  // Rotate 180° for Black so each player's own pieces sit at the bottom of their screen.
  const grid = playerColor === 'black' ? parsedGrid.slice().reverse().map(row => row.slice().reverse()) : parsedGrid;
  const isMyTurn = status === 'active' && turn === playerColor;
  const flipped = playerColor === 'black';

  const [selectedSquare, setSelectedSquare] = useState<string | null>(null);
  const [legalDestinations, setLegalDestinations] = useState<string[]>([]);
  const [preparedMove, setPreparedMove] = useState<{ from: string; to: string; promotion?: string } | null>(null);
  const [promotionChoice, setPromotionChoice] = useState<{ from: string; to: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [gameActionPending, setGameActionPending] = useState(false);
  const [gameActionError, setGameActionError] = useState<string | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const menuEnabled = status === 'active';

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current !== null) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleContextMenu = (event: MouseEvent) => {
    event.preventDefault();
    if (menuEnabled) setMenuOpen(true);
  };

  const handleTouchStart = () => {
    if (!menuEnabled) return;
    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => setMenuOpen(true), LONG_PRESS_MS);
  };

  const handleResign = async () => {
    setMenuOpen(false);
    setGameActionError(null);
    setGameActionPending(true);
    try {
      const response = await client.resignGame(gameId, token);
      if (response.ok) {
        onGameStateUpdate({
          fen: response.fen,
          turn: response.turn,
          move_count: response.move_count,
          status: response.status,
          result: response.result,
          result_reason: response.result_reason,
          last_move: response.last_move,
          draw_offered_by: undefined,
        });
      } else {
        setGameActionError(response.error);
      }
    } catch {
      setGameActionError('Failed to resign. Please try again.');
    } finally {
      setGameActionPending(false);
    }
  };

  const handleOfferDraw = async () => {
    setMenuOpen(false);
    setGameActionError(null);
    setGameActionPending(true);
    try {
      const response = await client.offerDraw(gameId, token);
      if (response.ok) {
        onGameStateUpdate({ draw_offered_by: playerColor });
      } else {
        setGameActionError(response.error);
      }
    } catch {
      setGameActionError('Failed to offer draw. Please try again.');
    } finally {
      setGameActionPending(false);
    }
  };

  const handleAcceptDraw = async () => {
    setGameActionError(null);
    setGameActionPending(true);
    try {
      const response = await client.acceptDraw(gameId, token);
      if (response.ok) {
        onGameStateUpdate({
          fen: response.fen,
          turn: response.turn,
          move_count: response.move_count,
          status: response.status,
          result: response.result,
          result_reason: response.result_reason,
          last_move: response.last_move,
          draw_offered_by: undefined,
        });
      } else {
        setGameActionError(response.error);
      }
    } catch {
      setGameActionError('Failed to accept draw. Please try again.');
    } finally {
      setGameActionPending(false);
    }
  };

  const handleRejectDraw = async () => {
    setGameActionError(null);
    setGameActionPending(true);
    try {
      const response = await client.rejectDraw(gameId, token);
      if (response.ok) {
        onGameStateUpdate({ draw_offered_by: undefined });
      } else {
        setGameActionError(response.error);
      }
    } catch {
      setGameActionError('Failed to reject draw. Please try again.');
    } finally {
      setGameActionPending(false);
    }
  };

  // A new board position invalidates any in-progress selection.
  useEffect(() => {
    setSelectedSquare(null);
    setPreparedMove(null);
    setPromotionChoice(null);
  }, [fen]);

  // Submits a move to the server. Called directly from event handlers (not a useEffect
  // reacting to preparedMove) so React StrictMode's dev-mode double-invocation can't fire
  // it twice and race two real network requests against each other.
  const submitMove = async (from: string, to: string, promotion?: string) => {
    setPreparedMove({ from, to, promotion });
    setSubmitting(true);

    try {
      const response = await client.executeMove(gameId, from, to, token, promotion);
      if (response.ok) {
        onMoveApplied({ fen: response.fen_result, from, to });
      } else if (response.promotion_required && response.move) {
        setPreparedMove(null);
        setPromotionChoice({ from: response.move.from_square, to: response.move.to_square });
      } else {
        setMoveError(response.error);
        setPreparedMove(null);
      }
    } catch {
      setMoveError('Failed to submit move. Please try again.');
      setPreparedMove(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePromotionSelect = (promotion: 'q' | 'r' | 'b' | 'n') => {
    if (!promotionChoice) return;
    const { from, to } = promotionChoice;
    setPromotionChoice(null);
    void submitMove(from, to, promotion);
  };

  useEffect(() => {
    if (!selectedSquare) {
      setLegalDestinations([]);
      return;
    }

    let cancelled = false;

    client
      .getLegalMoves(gameId, selectedSquare, token)
      .then(response => {
        if (!cancelled) setLegalDestinations(response.destinations);
      })
      .catch(error => {
        console.error('Failed to fetch legal moves:', error);
        if (!cancelled) {
          setSelectedSquare(null);
          setLegalDestinations([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedSquare, gameId, token, client]);

  const handleSquareClick = (rankIndex: number, fileIndex: number) => {
    if (!isMyTurn || submitting || preparedMove || promotionChoice) return;

    setMoveError(null);

    const square = squareAt(rankIndex, fileIndex, flipped);
    const piece = grid[rankIndex][fileIndex];
    const isOwnPiece = piece?.color === playerColor;

    if (!selectedSquare) {
      if (isOwnPiece) setSelectedSquare(square);
      return;
    }

    if (square === selectedSquare) {
      setSelectedSquare(null);
      return;
    }

    if (legalDestinations.includes(square)) {
      const from = selectedSquare;
      setSelectedSquare(null);
      void submitMove(from, square);
      return;
    }

    setSelectedSquare(isOwnPiece ? square : null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <div style={{ marginBottom: '16px', textAlign: 'center' }}>
        <p style={{ fontSize: '1.1rem', fontWeight: 'bold', margin: 0 }}>
          You are playing as {playerColor === 'white' ? 'White' : 'Black'}
        </p>
        {status === 'active' && (
          <p
            style={{
              fontSize: '1rem',
              margin: '4px 0 0 0',
              color: isMyTurn ? '#28a745' : '#666',
              fontWeight: isMyTurn ? 'bold' : 'normal',
            }}
          >
            {isMyTurn ? (submitting ? 'Submitting move...' : 'Your turn') : "Opponent's turn"}
          </p>
        )}
        {moveError && (
          <p style={{ fontSize: '0.9rem', margin: '8px 0 0 0', color: '#cc0000' }}>{moveError}</p>
        )}
        {gameActionError && (
          <p style={{ fontSize: '0.9rem', margin: '8px 0 0 0', color: '#cc0000' }}>{gameActionError}</p>
        )}
      </div>

      {status === 'active' && drawOfferedBy && (
        <div
          style={{
            backgroundColor: '#fff8e6',
            border: '2px solid #ff9800',
            borderRadius: '8px',
            padding: '12px 20px',
            marginBottom: '16px',
            textAlign: 'center',
          }}
        >
          {drawOfferedBy === playerColor ? (
            <p style={{ margin: 0, fontSize: '0.95rem', color: '#a15c00' }}>Draw offered — waiting for opponent</p>
          ) : (
            <>
              <p style={{ margin: '0 0 10px 0', fontSize: '0.95rem', fontWeight: 'bold', color: '#a15c00' }}>
                Draw offered
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  onClick={handleAcceptDraw}
                  disabled={gameActionPending}
                  style={{
                    padding: '8px 16px',
                    fontSize: '0.9rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: gameActionPending ? 'default' : 'pointer',
                    fontWeight: 'bold',
                  }}
                >
                  Accept
                </button>
                <button
                  onClick={handleRejectDraw}
                  disabled={gameActionPending}
                  style={{
                    padding: '8px 16px',
                    fontSize: '0.9rem',
                    backgroundColor: '#cc0000',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: gameActionPending ? 'default' : 'pointer',
                    fontWeight: 'bold',
                  }}
                >
                  Reject
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <div
        onContextMenu={handleContextMenu}
        onTouchStart={handleTouchStart}
        onTouchEnd={clearLongPressTimer}
        onTouchMove={clearLongPressTimer}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(8, minmax(0, 1fr))',
          gridTemplateRows: 'repeat(8, minmax(0, 1fr))',
          width: 'min(90vw, 480px)',
          height: 'min(90vw, 480px)',
          border: '3px solid #333',
        }}
      >
        {grid.map((row, rankIndex) =>
          row.map((square, fileIndex) => {
            const isLightSquare = (rankIndex + fileIndex) % 2 === 0;
            const symbol = square ? PIECE_SYMBOLS[`${square.color}_${square.type}`] : null;
            const squareName = squareAt(rankIndex, fileIndex, flipped);
            const isSelected = squareName === selectedSquare;
            const isLegalDestination = legalDestinations.includes(squareName);
            const isPrepared = preparedMove?.from === squareName || preparedMove?.to === squareName;
            const isClickable = isMyTurn && !submitting && (square?.color === playerColor || isLegalDestination);

            let ringColor: string | null = null;
            if (isPrepared) ringColor = 'rgba(0, 102, 204, 0.85)';
            else if (isSelected) ringColor = 'rgba(255, 193, 7, 0.85)';
            else if (isLegalDestination) ringColor = 'rgba(40, 167, 69, 0.75)';

            return (
              <div
                key={`${rankIndex}-${fileIndex}`}
                onClick={() => handleSquareClick(rankIndex, fileIndex)}
                data-testid={`square-${squareName}`}
                style={{
                  position: 'relative',
                  backgroundColor: isLightSquare ? '#f0d9b5' : '#b58863',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'min(8vw, 42px)',
                  lineHeight: 1,
                  color: square?.color === 'white' ? '#fff' : '#111',
                  textShadow: square?.color === 'white' ? '0 0 2px #000, 0 0 2px #000' : 'none',
                  userSelect: 'none',
                  cursor: isClickable ? 'pointer' : 'default',
                  boxShadow: ringColor ? `inset 0 0 0 4px ${ringColor}` : 'none',
                }}
              >
                {symbol}
                {isLegalDestination && !isPrepared && (
                  <span
                    style={{
                      position: 'absolute',
                      width: symbol ? '90%' : '28%',
                      height: symbol ? '90%' : '28%',
                      borderRadius: '50%',
                      border: symbol ? '4px solid rgba(40, 167, 69, 0.75)' : 'none',
                      backgroundColor: symbol ? 'transparent' : 'rgba(40, 167, 69, 0.55)',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            );
          })
        )}
      </div>

      {promotionChoice && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              padding: '20px',
              textAlign: 'center',
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            }}
          >
            <p style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 'bold' }}>Promote pawn to:</p>
            <div style={{ display: 'flex', gap: '10px' }}>
              {(['q', 'r', 'b', 'n'] as const).map(piece => (
                <button
                  key={piece}
                  onClick={() => handlePromotionSelect(piece)}
                  aria-label={`Promote to ${piece}`}
                  style={{
                    width: '56px',
                    height: '56px',
                    fontSize: '2rem',
                    border: '2px solid #0066cc',
                    borderRadius: '8px',
                    backgroundColor: '#f0f8ff',
                    cursor: 'pointer',
                  }}
                >
                  {PIECE_SYMBOLS[`${playerColor}_${piece}`]}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              padding: '8px',
              width: 'min(90vw, 260px)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            }}
          >
            <button
              onClick={handleResign}
              disabled={gameActionPending}
              style={menuButtonStyle}
            >
              Resign
            </button>
            <button
              onClick={handleOfferDraw}
              disabled={gameActionPending || Boolean(drawOfferedBy)}
              style={menuButtonStyle}
            >
              Offer Draw
            </button>
            <button onClick={() => setMenuOpen(false)} style={{ ...menuButtonStyle, color: '#666' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const menuButtonStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '14px 16px',
  fontSize: '1rem',
  textAlign: 'left',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};
